/**
 * Static WADM configuration values for EU e-waste stations
 * (waste codes, container types, weight units).
 *
 * No API exists today to fetch these dynamically — follow-up tracked in
 * Taskei P435716970 to expose them via OculusService backend (and/or WADM).
 */

/**
 * Country-specific waste codes. The codes shown depend on the user's locale:
 *   - ES gets extended sub-codes
 *   - DE, UK, FR, IT (and all others) get the standard 4 codes
 *
 * `value` is the canonical (spaced) EWC code and is what the UI displays and what is stored
 * as the item's `ewcCode` (WTN / compliance documents expect the spaced form). What is SENT
 * as the WADM `wasteProfileId` is the compact form produced by {@link toWasteProfileId}.
 */
export const WASTE_CODES = Object.freeze({
    DEFAULT: [
        {label: "20 01 36", value: "20 01 36"},
        {label: "20 01 35*", value: "20 01 35*"},
        {label: "20 01 23*", value: "20 01 23*"},
        {label: "20 03 07", value: "20 03 07"}
    ],
    ES: [
        {label: "20 01 36 - 23", value: "20 01 36 - 23"},
        {label: "20 01 36 - 42", value: "20 01 36 - 42"},
        {label: "20 01 35* - 22*", value: "20 01 35* - 22*"},
        {label: "20 01 35* - 21*", value: "20 01 35* - 21*"},
        {label: "20 01 23* - 11*", value: "20 01 23* - 11*"},
        {label: "20 01 23* - 12*", value: "20 01 23* - 12*"},
        {label: "20 03 07", value: "20 03 07"}
    ]
});

/**
 * Returns the waste codes array for the given locale string.
 * @param {string} locale — e.g. "es", "es-ES", "de", "en-GB"
 */
export function getWasteCodesForLocale(locale) {
    const lang = (locale || "").split(/[-_]/)[0].toLowerCase();
    return lang === "es" ? WASTE_CODES.ES : WASTE_CODES.DEFAULT;
}

/**
 * Converts a canonical EWC code into the compact form sent to WADM as `wasteProfileId`.
 *
 * WADM derives the container display ID as `<first 6 chars of wasteProfileId>-<uuid[0:4]>`
 * and does no normalisation of its own, so a spaced code ("20 01 36") yields a display ID
 * with embedded spaces ("20 01 -a1b2") that Rabbit iOS rejects and that collides across
 * codes. Removing whitespace ("200136", "200135*", "200136-23") keeps all six EWC digits
 * and the hazardous marker while producing a clean display ID ("200136-a1b2").
 *
 * Only whitespace is removed — never digits, "*" or the ES "-NN" suffix.
 *
 * @param {string} code canonical EWC code, e.g. "20 01 35*" or "20 01 36 - 23"
 * @returns {string} compact WADM wasteProfileId, e.g. "200135*" or "200136-23"
 */
export function toWasteProfileId(code) {
    return code == null ? "" : String(code).replace(/\s+/g, "");
}

/**
 * Inverse of {@link toWasteProfileId}: maps a WADM `wasteProfileId` (compact, or a legacy
 * spaced value stored before compaction) back to the canonical spaced EWC code for display
 * and for the item's `ewcCode`. Unknown values are returned trimmed but otherwise unchanged
 * so nothing is ever hidden.
 *
 * @param {string} wasteProfileId e.g. "200135*", "20 01 35*" or "200136-23"
 * @returns {string} canonical code, e.g. "20 01 35*" or "20 01 36 - 23"
 */
export function toCanonicalWasteCode(wasteProfileId) {
    if (wasteProfileId == null) {
        return "";
    }
    const compact = toWasteProfileId(wasteProfileId);
    const match = [...WASTE_CODES.DEFAULT, ...WASTE_CODES.ES]
        .find((c) => toWasteProfileId(c.value) === compact);
    return match ? match.value : String(wasteProfileId).trim();
}

// TODO: move container type mapping in Oculus backend (https://taskei.amazon.dev/tasks/P435716970)
export const CONTAINER_TYPES = Object.freeze([
    {label: "Container 20ft Metal", value: "c11a4d69-ae99-468c-9dd9-ef7d28f75020"},
    {label: "Container 40ft Metal", value: "7cfe0979-6a38-449d-a45d-b45e64c2b298"},
    {label: "30 m3 Metal", value: "b9e52256-f675-498d-a1dc-e0ea568f6fb2"},
    {label: "36 m3 Metal", value: "fab08377-075c-4842-b1ad-18962035e3e3"},
    {label: "Gitterbox Metal", value: "2af38c48-963e-43cd-940f-5d7e31661ce9"}
]);

export const WEIGHT_UNITS = Object.freeze(["KILOGRAM", "POUND", "GRAM"]);

export const WEIGHT_UNIT_LABELS = Object.freeze({
    KILOGRAM: "kg",
    POUND: "lb",
    GRAM: "g"
});

export const INITIAL_CREATE_FORM = Object.freeze({
    wasteProfileId: "",
    containerTypeId: "",
    maxWeightValue: "",
    maxWeightUnit: ""
});

export const INITIAL_ITEM_FORM = Object.freeze({
    itemName: "",
    quantity: "",
    weightValue: "",
    weightUnit: ""
});



// WEBPACK FOOTER //
// ./src/commons/constants/EWasteConfig.js